import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AiChat,
  AiChatMessage,
  ChatContextComponents,
  ChatShort,
} from '../common/ai-chat/aiChatTypes';
import { RootState } from '../../reducers';
import { AgentType } from '@server/ai';

interface AiChatsOrg {
  chatsList: {
    isLoadingChatList: boolean; //used to show loader of the list
    data?: ChatShort[];
  };
  chatsDetails: {
    [chatId: string]: {
      isLoadingChat: boolean; //used to show loader of the chat area
      data?: AiChat;
    };
  };
  activeChatId?: string;
  search?: {
    searchText?: string;
    results?: AiChat[];
    isLoading?: boolean;
  };
}

const TEMP_CHAT_ID = 'TEMP_CHAT_ID';
const TEMP_ORG_ID = 'TEMP_ORG_ID';

const getOrgInitialState: () => AiChatsOrg = () => ({
  chatsList: {
    isLoadingChatList: false,
  },
  chatsDetails: {},
  activeChatId: TEMP_CHAT_ID,
});

const getChatInitialState: ({
  agentId,
  agentType,
  message,
  isLoadingContext,
}: {
  agentId: string;
  agentType: AgentType;
  isLoadingContext?: boolean;
  message?: AiChatMessage;
}) => AiChat = ({ agentId, agentType, message, isLoadingContext }) => ({
  id: TEMP_CHAT_ID,
  name: 'New chat',
  aiChatDetails: { messages: message && message.content ? [message as AiChatMessage] : [] },
  isFavorite: false,
  updatedAt: new Date().toISOString(),
  isLoadingResponse: false,
  isLoadingContext: !!isLoadingContext,
  agentId,
  agentType,
});

export interface AiChatsState {
  environments: {
    [crmOrgId: string]: AiChatsOrg;
  };
}

const initialState: AiChatsState = {
  environments: {},
};

export const aiChatsSlice = createSlice({
  name: 'aiChats',
  initialState,
  reducers: {
    createGhostChat: (
      state,
      action: PayloadAction<{
        crmOrgId?: string | null;
        agentType: AgentType;
        agentId: string;
        isLoadingContext?: boolean;
      }>,
    ) => {
      const { crmOrgId, agentType, agentId, isLoadingContext } = action.payload;
      const _crmOrgId = crmOrgId ?? TEMP_ORG_ID;
      if (!state.environments[_crmOrgId]) {
        state.environments[_crmOrgId] = getOrgInitialState();
      }
      state.environments[_crmOrgId].activeChatId = TEMP_CHAT_ID;
      state.environments[_crmOrgId].chatsDetails[TEMP_CHAT_ID] = {
        isLoadingChat: false,
        data: getChatInitialState({ agentId, agentType, isLoadingContext }),
      };
    },
    setActiveChat: (
      state,
      action: PayloadAction<{ crmOrgId?: string | null; chatId?: string }>,
    ) => {
      const { chatId, crmOrgId } = action.payload;
      const _crmOrgId = crmOrgId ?? TEMP_ORG_ID;

      if (!state.environments[_crmOrgId]) {
        state.environments[_crmOrgId] = getOrgInitialState();
      }
      state.environments[_crmOrgId].activeChatId = chatId;
    },
    setLoadingChatDetails: (state, action: PayloadAction<{ crmOrgId: string; chatId: string }>) => {
      const { chatId, crmOrgId } = action.payload;
      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = getOrgInitialState();
      }
      if (!state.environments[crmOrgId].chatsDetails[chatId]) {
        state.environments[crmOrgId].chatsDetails[chatId] = { isLoadingChat: true };
      }
      state.environments[crmOrgId].chatsDetails[chatId].isLoadingChat = true;
    },
    setChat: (state, action: PayloadAction<{ crmOrgId: string; chat: AiChat }>) => {
      const { chat, crmOrgId } = action.payload;
      const { id } = chat;
      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = getOrgInitialState();
      }
      state.environments[crmOrgId].chatsDetails[id] = { isLoadingChat: false, data: chat };
    },
    replaceTempWithChatIdAndName: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        chatId: string;
        name: string;
        agentId: string;
        agentType: AgentType;
      }>,
    ) => {
      const { chatId, crmOrgId, name, agentId, agentType } = action.payload;
      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = getOrgInitialState();
      }
      const relevantChat = state.environments[crmOrgId].chatsDetails[TEMP_CHAT_ID]?.data;
      if (!relevantChat) {
        return;
      }
      if (!state.environments[crmOrgId].chatsList) {
        state.environments[crmOrgId].chatsList = { isLoadingChatList: false, data: [] };
      }
      if (!state.environments[crmOrgId].chatsList.data) {
        state.environments[crmOrgId].chatsList.data = [];
      }
      state.environments[crmOrgId].chatsList.data.push({
        id: chatId,
        name,
        updatedAt: new Date().toISOString(),
        isFavorite: false,
        isLoading: false,
        isAnimateName: true,
        agentId,
        agentType,
      });
      state.environments[crmOrgId].chatsDetails[chatId] = {
        isLoadingChat: false,
        data: {
          ...relevantChat,
          id: chatId,
        },
      };
      delete state.environments[crmOrgId].chatsDetails[TEMP_CHAT_ID];
      state.environments[crmOrgId].activeChatId = chatId;
    },
    resetAnimateName: (state, action: PayloadAction<{ crmOrgId: string; chatId: string }>) => {
      const { chatId, crmOrgId } = action.payload;
      if (!state.environments[crmOrgId]?.chatsList?.data) {
        return;
      }
      const relevantChatItem = state.environments[crmOrgId].chatsList.data.find(
        (chat) => chat.id === chatId,
      );
      if (!relevantChatItem) {
        return;
      }
      relevantChatItem.isAnimateName = false;
    },
    addSyncMessageToActiveChat: (
      state,
      action: PayloadAction<{
        crmOrgId?: string | null;
        message: AiChatMessage;
        agentId: string;
        agentType: AgentType;
      }>,
    ) => {
      const { message, crmOrgId, agentId, agentType } = action.payload;
      //crmOrgId is not mandatory, to allow adding a message even if there's no crmOrg connected
      if (!crmOrgId) {
        state.environments[TEMP_ORG_ID] = getOrgInitialState();
        state.environments[TEMP_ORG_ID].activeChatId = TEMP_CHAT_ID;
      } else {
        if (!state.environments[crmOrgId]) {
          state.environments[crmOrgId] = getOrgInitialState();
        }
      }

      const _crmOrgId = crmOrgId ?? TEMP_ORG_ID;

      const chatId = state.environments[_crmOrgId].activeChatId;
      if (!chatId) {
        return;
      }
      if (!state.environments[_crmOrgId].chatsDetails[chatId]) {
        state.environments[_crmOrgId].chatsDetails[chatId] = {
          isLoadingChat: false,
          data: getChatInitialState({ agentId, agentType }),
        };
      }
      if (!state.environments[_crmOrgId].chatsDetails[chatId].data) {
        state.environments[_crmOrgId].chatsDetails[chatId].data = getChatInitialState({
          agentId,
          agentType,
        });
      }
      if (message.content) {
        state.environments[_crmOrgId].chatsDetails[chatId].data.aiChatDetails.messages.push(
          message as AiChatMessage,
        );
      }
      state.environments[_crmOrgId].chatsDetails[chatId].data.isLoadingResponse = !!crmOrgId; //if there's no crmOrg, there's no call to server
    },
    addAsyncResponse: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        chatId?: string;
        message: AiChatMessage;
      }>,
    ) => {
      const { message, crmOrgId, chatId } = action.payload;
      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = getOrgInitialState();
      }

      const _chatId = chatId ?? state.environments[crmOrgId].activeChatId;
      if (!_chatId) {
        return;
        //this shouldn't happen
      }

      if (!state.environments[crmOrgId].chatsDetails[_chatId]?.data?.aiChatDetails.messages) {
        return;
        //this shouldn't happen
      }
      state.environments[crmOrgId].chatsDetails[_chatId].isLoadingChat = false;
      state.environments[crmOrgId].chatsDetails[_chatId].data.isLoadingResponse = false;
      state.environments[crmOrgId].chatsDetails[_chatId].data.aiChatDetails.messages.push(message);
    },
    setChatContextForGhostChat: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        chatContextComponents?: ChatContextComponents;
      }>,
    ) => {
      const { crmOrgId, chatContextComponents } = action.payload;
      if (
        !state.environments[crmOrgId] ||
        !state.environments[crmOrgId].chatsDetails[TEMP_CHAT_ID] ||
        !state.environments[crmOrgId].chatsDetails[TEMP_CHAT_ID].data
      ) {
        return;
      }
      state.environments[crmOrgId].chatsDetails[TEMP_CHAT_ID].data.chatContextComponents =
        chatContextComponents;
      state.environments[crmOrgId].chatsDetails[TEMP_CHAT_ID].data.isLoadingContext = false;
    },
    setLoadingChatsList: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
      }>,
    ) => {
      const { crmOrgId } = action.payload;
      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = getOrgInitialState();
      }
      state.environments[crmOrgId].chatsList.isLoadingChatList = true;
      state.environments[crmOrgId].chatsList.data = undefined;
    },
    setChatsList: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        chats: ChatShort[];
      }>,
    ) => {
      const { crmOrgId, chats } = action.payload;
      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = getOrgInitialState();
      }
      state.environments[crmOrgId].chatsList.isLoadingChatList = false;
      state.environments[crmOrgId].chatsList.data = chats;
    },
    setChatName: (
      state,
      action: PayloadAction<{ crmOrgId: string; chatId: string; name: string }>,
    ) => {
      const { crmOrgId, chatId, name } = action.payload;
      if (!state.environments[crmOrgId] || !state.environments[crmOrgId]?.chatsList.data) {
        return;
      }
      const relevantChat = state.environments[crmOrgId].chatsList.data.find(
        (chat) => chat.id === chatId,
      );
      if (!relevantChat) {
        return;
      }
      relevantChat.name = name;
      relevantChat.isLoading = false;
      relevantChat.isAnimateName = false; //animation is only for the auto-rename
    },
    setLoadingChatListItem: (
      state,
      action: PayloadAction<{ crmOrgId: string; chatId: string }>,
    ) => {
      const { crmOrgId, chatId } = action.payload;
      if (!state.environments[crmOrgId] || !state.environments[crmOrgId]?.chatsList?.data) {
        return;
      }
      const relevantChat = state.environments[crmOrgId].chatsList.data.find(
        (chat) => chat.id === chatId,
      );
      if (!relevantChat) {
        return;
      }
      relevantChat.isLoading = true;
    },
    deleteChat: (state, action: PayloadAction<{ crmOrgId: string; chatId: string }>) => {
      const { crmOrgId, chatId } = action.payload;
      if (!state.environments[crmOrgId] || !state.environments[crmOrgId]?.chatsList?.data) {
        return;
      }
      if (state.environments[crmOrgId].activeChatId === chatId) {
        state.environments[crmOrgId].activeChatId = TEMP_CHAT_ID;
      }
      state.environments[crmOrgId].chatsList.data = state.environments[
        crmOrgId
      ].chatsList.data.filter((chat) => chat.id !== chatId);
    },
    deleteChatsForAgent: (state, action: PayloadAction<{ crmOrgId: string; agentId: string }>) => {
      const { crmOrgId, agentId } = action.payload;
      if (!state.environments[crmOrgId] || !state.environments[crmOrgId]?.chatsList?.data) {
        return;
      }
      const activeChatId = state.environments[crmOrgId].activeChatId;
      if (activeChatId) {
        const activeChatAgentId = state.environments[crmOrgId].chatsList.data.find(
          (chat) => chat.id === activeChatId,
        )?.agentId;
        if (activeChatAgentId === agentId) {
          state.environments[crmOrgId].activeChatId = TEMP_CHAT_ID;
        }
      }
      state.environments[crmOrgId].chatsList.data = state.environments[
        crmOrgId
      ].chatsList.data.filter((chat) => chat.agentId !== agentId);
    },
    setChatIsFavorite: (
      state,
      action: PayloadAction<{ crmOrgId: string; chatId: string; isFavorite: boolean }>,
    ) => {
      const { crmOrgId, chatId, isFavorite } = action.payload;
      if (!state.environments[crmOrgId] || !state.environments[crmOrgId]?.chatsList?.data) {
        return;
      }
      const relevantChat = state.environments[crmOrgId].chatsList.data.find(
        (chat) => chat.id === chatId,
      );
      if (relevantChat) {
        relevantChat.isFavorite = isFavorite;
      }
    },
    setLoadingSearch: (state, action: PayloadAction<{ crmOrgId: string; searchText: string }>) => {
      const { crmOrgId, searchText } = action.payload;
      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = getOrgInitialState();
      }
      state.environments[crmOrgId].search = {
        isLoading: true,
        searchText,
      };
    },
    setSearchResult: (state, action: PayloadAction<{ crmOrgId: string; chats: AiChat[] }>) => {
      const { crmOrgId, chats } = action.payload;
      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = getOrgInitialState();
      }
      if (!state.environments[crmOrgId].search) {
        state.environments[crmOrgId].search = {};
      }
      state.environments[crmOrgId].search.isLoading = false;
      state.environments[crmOrgId].search.results = chats;
    },
    clearSearch: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const { crmOrgId } = action.payload;
      if (state.environments[crmOrgId]?.search) {
        state.environments[crmOrgId].search = {
          isLoading: false,
        };
      }
    },
  },
});

export const {
  createGhostChat,
  setActiveChat,
  replaceTempWithChatIdAndName,
  addAsyncResponse,
  addSyncMessageToActiveChat,
  setLoadingChatsList,
  setChatsList,
  setLoadingChatDetails,
  setChat,
  setLoadingChatListItem,
  setChatName,
  deleteChat,
  deleteChatsForAgent,
  setChatIsFavorite,
  setLoadingSearch,
  setSearchResult,
  clearSearch,
  resetAnimateName,
  setChatContextForGhostChat,
} = aiChatsSlice.actions;

export const selectActiveChatId = (crmOrgId?: string) => (state: RootState) => {
  return state.aiChats.environments[crmOrgId ?? TEMP_ORG_ID]?.activeChatId;
};

export const selectChatsListData = (crmOrgId?: string) => (state: RootState) => {
  return state.aiChats.environments[crmOrgId ?? TEMP_ORG_ID]?.chatsList.data;
};

export const selectActiveIsEmptyChat = (crmOrgId?: string) => (state: RootState) => {
  return state.aiChats.environments[crmOrgId ?? TEMP_ORG_ID]?.activeChatId === TEMP_CHAT_ID;
};

export const selectChatsListIsLoading = (crmOrgId?: string) => (state: RootState) => {
  return state.aiChats.environments[crmOrgId ?? TEMP_ORG_ID]?.chatsList.isLoadingChatList;
};

export const selectChatsDetails = (crmOrgId?: string) => (state: RootState) => {
  return state.aiChats.environments[crmOrgId ?? TEMP_ORG_ID]?.chatsDetails;
};

export const selectSearchIsLoading = (crmOrgId?: string) => (state: RootState) => {
  if (crmOrgId) {
    return state.aiChats.environments[crmOrgId]?.search?.isLoading;
  }
};

export const selectSearchText = (crmOrgId?: string) => (state: RootState) => {
  if (crmOrgId) {
    return state.aiChats.environments[crmOrgId]?.search?.searchText;
  }
};

export const selectSearchResults = (crmOrgId?: string) => (state: RootState) => {
  if (crmOrgId) {
    return state.aiChats.environments[crmOrgId]?.search?.results;
  }
};

export default aiChatsSlice.reducer;
